// Tarif (Q-022, Q-081): restoran nima bilan ishlaydi. Tarifdagi qurilmalar narx ichida, ortig'i — qo'shimcha (oylik summaga).
// Ega o'zi hech narsani yoqa olmaydi — hammasi shu yerdan (to'lov bilan bog'liq).
import { useState } from 'react';
import { LIMIT_UNITS, addonPack, quoteMonthly } from '@onepos/shared';
import { Alert, Badge, Button, Card, Spinner, TextField, useAction, useAsync } from '@onepos/ui';
import { MonthlyPreview } from '../../components/MonthlyPreview';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { fmtMoney } from '../../lib/format';
import type { TenantDetail } from '../../lib/types';

interface Tariff {
  plan: { id: string; label: string; limits: Record<string, number>; max_branches: number; max_staff: number };
  overrides: Record<string, number>;
  limits: Record<string, { max: number; used: number; custom: boolean }>;
  owner_devices: number;
  staff: { max: number; used: number };
}

const HINTS: Record<string, string> = {
  pos: 'Savdo oynasi',
  kds: '“Oshxona ekrani” bo‘limi yoqilgan bo‘lishi kerak',
  printer: 'Oshxona printeri (chek printeri cheklanmaydi)',
  queue: 'Mijozlarga buyurtma holati ekrani',
  waiter: 'Ofitsiant telefoni — “Ofitsiant ilovasi” bo‘limi bilan',
};
const KEYS = Object.keys(LIMIT_UNITS);
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

export function TariffTab({ data, reload }: { data: TenantDetail; reload: () => void }) {
  const me = useMe();
  const canEdit = me.role === 'owner' || me.role === 'billing';
  const t = useAsync(() => api.rpc<Tariff>('platform_tenant_tariff', { p_tenant: data.tenant.id }), [data.tenant.id]);
  // Qo'shimcha (tarifdan ortiq) son — forma qiymati
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const save = useAction(async () => {
    const payload: Record<string, number | null> = {};
    for (const k of KEYS) {
      if (!(k in draft)) continue;
      const extra = Number(draft[k]!.trim() || '0');
      payload[k] = extra > 0 ? (data.plan.limits[k] ?? 0) + extra : null;
    }
    await api.rpc('platform_set_limits', { p_tenant: data.tenant.id, p_limits: payload, p_reason: reason || null });
    setDraft({});
    setReason('');
    t.reload();
    reload();
  });

  if (t.loading && !t.data) return <Spinner />;
  if (t.error) return <Alert tone="danger">{t.error.message}</Alert>;
  const tf = t.data;
  if (!tf) return null;
  const dirty = Object.keys(draft).length > 0;
  const prices = data.plan.addon_prices ?? {};
  const privileged = data.tenant.privileged === true;

  // Amaldagi qo'shimcha: restoranga yozilgan son − tarifdagi
  const currentExtra = (k: string) => Math.max(0, (tf.overrides[k] ?? tf.plan.limits[k] ?? 0) - (tf.plan.limits[k] ?? 0));
  const extraOf = (k: string) => (k in draft ? Number(draft[k] || '0') : currentExtra(k));
  const extras = Object.fromEntries(KEYS.map((k) => [k, extraOf(k)]));
  const quote = quoteMonthly(data.plan, {
    extra: extras,
    modules: data.modules.filter((m) => m.enabled).map((m) => m.key),
    branches: data.branches.filter((b) => b.is_active).length,
    privileged,
  });
  const invalid = Object.values(draft).some((v) => v !== '' && Number(v) > 999);

  return (
    <Card title={`Tarif: ${tf.plan.label}`}>
      <p className="cn-muted" style={{ marginTop: 0 }}>
        Tarifdagi qurilmalar narx ichida. Ko‘proq kerak bo‘lsa — “Qo‘shimcha” ustuniga sonini yozing: narxi qo‘yilgan bo‘lsa, oylik summaga qo‘shiladi.
        Ega profili har doim bor (istalgan qurilmada, limitsiz) — hozir {tf.owner_devices} ta ega qurilmasi ulangan.
      </p>
      <table className="cn-table">
        <thead>
          <tr>
            <th>Qurilma</th>
            <th className="cn-num">Tarifda</th>
            <th className="cn-num">Qo‘shimcha</th>
            <th className="cn-num">Jami</th>
            <th className="cn-num">Band</th>
            <th className="cn-num">Narxi, oyiga</th>
          </tr>
        </thead>
        <tbody>
          {KEYS.map((k) => {
            const l = tf.limits[k] ?? { max: 0, used: 0, custom: false };
            const base = tf.plan.limits[k] ?? 0;
            const restricted = l.custom && (tf.overrides[k] ?? 0) < base;
            const extra = extraOf(k);
            const total = k in draft ? base + extra : privileged ? l.max : base + currentExtra(k);
            const price = prices[k];
            const pack = addonPack(data.plan, k);
            const value = k in draft ? draft[k]! : currentExtra(k) > 0 ? String(currentExtra(k)) : '';
            return (
              <tr key={k}>
                <td data-label="Qurilma">
                  <span className="cn-table__name">{cap(LIMIT_UNITS[k]!)}</span>
                  <span className="cn-table__sub">
                    {HINTS[k]}
                    {restricted && ` · restoranga kamaytirilgan: ${tf.overrides[k]}`}
                  </span>
                </td>
                <td data-label="Tarifda" className="cn-num op-num">
                  {base}
                </td>
                <td data-label="Qo‘shimcha" className="cn-num">
                  <input
                    className="cn-limit-input"
                    inputMode="numeric"
                    aria-label={`${LIMIT_UNITS[k]}: qo‘shimcha`}
                    placeholder="0"
                    value={value}
                    disabled={!canEdit}
                    onChange={(e) => setDraft({ ...draft, [k]: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                  />
                </td>
                <td data-label="Jami" className="cn-num op-num">
                  {privileged ? 'cheklovsiz' : restricted && !(k in draft) ? tf.overrides[k] : total}
                </td>
                <td data-label="Band" className="cn-num">
                  <Badge tone={l.max > 0 && l.used >= l.max ? 'warning' : 'neutral'}>{l.used}</Badge>
                </td>
                <td data-label="Narxi, oyiga" className="cn-num">
                  {price === undefined ? (
                    <span className="cn-muted">belgilanmagan — bepul</span>
                  ) : (
                    <span className="op-num">
                      {fmtMoney(price)}
                      {pack > 1 ? ` / ${pack} talik` : ''}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && dirty && (
        <div className="cn-row" style={{ marginTop: 14, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <TextField label="Sabab (jurnal uchun)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Masalan: 2-kassa to‘landi" />
          </div>
          <Button onClick={() => setDraft({})} disabled={save.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" onClick={() => void save.run()} disabled={save.busy || invalid}>
            Saqlash
          </Button>
        </div>
      )}
      {save.error && <Alert tone="danger">{save.error}</Alert>}
      <h3 className="cn-wsec__sub">{dirty ? 'Saqlangandan keyin oylik summa' : 'Oylik summa'}</h3>
      <MonthlyPreview quote={quote} planLabel={data.plan.label} privileged={privileged} />
      <p className="cn-muted" style={{ marginBottom: 0 }}>
        Kamaytirilsa, ishlab turgan qurilmalar uzilmaydi — faqat yangilari qo‘shilmaydi. Bo‘limlar “Bo‘limlar” bo‘limida yoqiladi. Narx kafolati
        bo‘lsa, aniq summa “Obuna” bo‘limida. Xodimlar: {tf.staff.used} / {tf.staff.max} · filiallar: {tf.plan.max_branches}.
      </p>
    </Card>
  );
}
